/**
 * reco-cart.js
 *
 * Defines the <cart-recommendations> custom element used by both the cart
 * page block (sections/reco-cart.liquid) and the cart drawer block
 * (snippets/cart-drawer.liquid). Moved out of sections/reco-cart.liquid's
 * inline <script> into this shared asset (Task 4) so the drawer - which can
 * render on any page, not just /cart - has access to the same class without
 * duplicating its logic.
 *
 * Layout is selected per-instance via the `data-layout` attribute:
 *   - "drawer": compact horizontal rows (see .reco-cart--drawer in
 *     reco-cart.css). Add-to-cart refreshes the "cart-drawer" and
 *     "cart-icon-bubble" sections (assets/cart-drawer.js's own
 *     getSectionsToRender() pattern) - which re-renders the whole drawer
 *     markup server-side, including a fresh <cart-recommendations> instance,
 *     so no explicit re-render call is needed/safe after that swap.
 *   - anything else (including absent): the original cart-page layout.
 *     Add-to-cart refreshes "cart-items"/"cart-footer"/"cart-icon-bubble"
 *     (assets/cart.js's pattern) and then explicitly re-renders this same
 *     element, since those sections don't include it.
 *
 * Drawer instances render LAZILY: on a page where the drawer is closed the
 * element connects, renders nothing, and only starts its cascade once the host
 * <cart-drawer> gains the "active" class (see _renderWhenDrawerOpens). That
 * makes the block independent of whether CartDrawer.open() happens to refresh
 * the "cart-drawer" section first - previously the only reason a freshly
 * opened drawer showed recommendations was that open() -> refreshCartContent()
 * re-rendered the drawer markup and thereby re-created this element. The
 * theme's assets/cart-drawer.js is mirrored read-only in the repo at
 * theme/_reference/cart-drawer.js for reference; it is NOT ours to modify and
 * is never uploaded.
 */
// Maintainer note: this asset (and reco-cart.css) is intentionally loaded from BOTH
// sections/reco-cart.liquid and snippets/cart-drawer.liquid - the customElements.get() guard below makes that safe, so don't "deduplicate" either include.
(function () {
  'use strict';

  if (window.customElements.get('cart-recommendations')) return;

  function debugLog() {
    try {
      if (window.console && typeof window.console.debug === 'function') {
        var args = ['[reco-cart]'].concat(Array.prototype.slice.call(arguments));
        window.console.debug.apply(window.console, args);
      }
    } catch (e) {
      // no-op
    }
  }

  function getShopCurrency() {
    try {
      if (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) {
        return window.Shopify.currency.active;
      }
    } catch (e) {
      // fall through
    }
    return 'RSD';
  }

  // Every other price on the page is Liquid `money` output in the shop's own money
  // format (e.g. "60,00 RSD" - `.` thousands, `,` decimal, two decimals, currency
  // code after a space), which does NOT follow the visitor's browser/storefront
  // locale. `style: 'currency'` would produce "RSD 60" here, so instead the major
  // amount is grouped/decimalled with a fixed 'sr-RS' formatter and the currency
  // code is appended manually, matching the shop format exactly regardless of
  // visitor locale.
  function formatPrice(minorUnits, currency) {
    var amount = (Number(minorUnits) || 0) / 100;
    try {
      return (
        new Intl.NumberFormat('sr-RS', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }).format(amount) +
        ' ' +
        currency
      );
    } catch (e) {
      return amount.toFixed(2) + ' ' + currency;
    }
  }

  function getCartAddUrl() {
    try {
      if (window.routes && window.routes.cart_add_url) return window.routes.cart_add_url;
    } catch (e) {
      // fall through
    }
    return '/cart/add.js';
  }

  function getCartUrl() {
    try {
      if (window.routes && window.routes.cart_url) return window.routes.cart_url;
    } catch (e) {
      // fall through
    }
    return '/cart';
  }

  // The JSON template keys used in templates/cart.json ("cart-items",
  // "cart-footer") are NOT valid Section Rendering API ids - Shopify's
  // ?section_id= endpoint 404s on them. The theme's own main-cart-items.liquid
  // / main-cart-footer.liquid embed the real runtime id in a data-id attribute
  // (data-id="{{ section.id }}", e.g. "template--xyz__cart-items"), which is
  // exactly what assets/cart.js reads via getSectionsToRender(). Falling back
  // to the section's file/type name ("main-cart-items" / "main-cart-footer")
  // also resolves correctly if the element/attribute is ever missing.
  function getRuntimeSectionId(elementId, fallbackId) {
    try {
      var el = document.getElementById(elementId);
      if (el && el.dataset && el.dataset.id) return el.dataset.id;
    } catch (e) {
      // fall through
    }
    return fallbackId;
  }

  // Measurement (assets/reco-track.js, loaded next to this file). Optional: if it is
  // missing, recommendations render and add to cart exactly as before, untracked.
  function tracker() {
    return window.MtgRecoTrack || null;
  }

  // Fetches one section's rendered HTML via the Section Rendering API
  // (?section_id=<id>), exactly the pattern the theme's own assets/cart.js
  // (CartItems.onCartUpdate), assets/cart-drawer.js (CartDrawer.refreshCartContent)
  // and assets/product-form.js (updateCartIcons) already use elsewhere.
  // Resolves null on any failure - callers treat a null section as "leave
  // that part of the DOM alone".
  function fetchSectionHTML(sectionId) {
    return window
      .fetch(getCartUrl() + '?section_id=' + encodeURIComponent(sectionId), { credentials: 'same-origin' })
      .then(function (res) {
        return res.ok ? res.text() : null;
      })
      .catch(function () {
        return null;
      });
  }

  // Shared by both layouts: applies a "cart-icon-bubble" section HTML fetch
  // (raw text from fetchSectionHTML) to the live #cart-icon-bubble element,
  // exactly mirroring assets/cart.js / assets/cart-drawer.js's own
  // "unwrap .shopify-section, swap innerHTML" pattern.
  function applyCartIconBubbleUpdate(html) {
    if (!html) return;
    try {
      var bubbleEl = document.getElementById('cart-icon-bubble');
      if (!bubbleEl) return;
      var bubbleDoc = new DOMParser().parseFromString(html, 'text/html');
      var wrapped = bubbleDoc.querySelector('.shopify-section');
      bubbleEl.innerHTML = wrapped ? wrapped.innerHTML : bubbleDoc.body.innerHTML;
    } catch (e) {
      debugLog('failed to apply cart-icon-bubble section update', e);
    }
  }

  /**
   * Applies a { 'cart-items': html, 'cart-footer': html, 'cart-icon-bubble': html }
   * map (each value the raw section HTML fetched via fetchSectionHTML) to the
   * live DOM, in place - no page reload. Mirrors the theme's own assets/cart.js
   * (CartItems.onCartUpdate / getSectionsToRender) pattern: swap the
   * <cart-items> custom element's content/class, the #main-cart-footer
   * wrapper's content/class, and the header's #cart-icon-bubble markup.
   * Used for the cart-page layout only - neither "cart-items" nor
   * "cart-footer" contains this <cart-recommendations> element, so it stays
   * connected and must be re-rendered separately by the caller.
   */
  function applySectionUpdates(sections) {
    if (!sections || typeof sections !== 'object') return;

    try {
      if (sections['cart-items']) {
        var itemsDoc = new DOMParser().parseFromString(sections['cart-items'], 'text/html');
        var freshCartItems = itemsDoc.querySelector('cart-items');
        var liveCartItems = document.querySelector('cart-items');
        if (freshCartItems && liveCartItems) {
          liveCartItems.className = freshCartItems.className;
          liveCartItems.innerHTML = freshCartItems.innerHTML;
        }
      }
    } catch (e) {
      debugLog('failed to apply cart-items section update', e);
    }

    try {
      if (sections['cart-footer']) {
        var footerDoc = new DOMParser().parseFromString(sections['cart-footer'], 'text/html');
        var freshFooter = footerDoc.getElementById('main-cart-footer');
        var liveFooter = document.getElementById('main-cart-footer');
        if (freshFooter && liveFooter) {
          liveFooter.className = freshFooter.className;
          liveFooter.innerHTML = freshFooter.innerHTML;
        }
      }
    } catch (e) {
      debugLog('failed to apply cart-footer section update', e);
    }

    applyCartIconBubbleUpdate(sections['cart-icon-bubble']);
  }

  /**
   * Drawer-layout equivalent of applySectionUpdates: refreshes the
   * "cart-drawer" section (which contains the entire drawer markup,
   * including this <cart-recommendations> element itself) and
   * "cart-icon-bubble", mirroring assets/cart-drawer.js's own
   * CartDrawer.refreshCartContent(). Because "cart-drawer" contains this
   * element, swapping #CartDrawer's innerHTML destroys the calling instance
   * and a fresh one (server-rendered, connectedCallback re-runs) takes its
   * place - so the caller must NOT also call _scheduleRender() on itself
   * after this returns.
   */
  function applyDrawerSectionUpdate(cartDrawerHtml, cartIconBubbleHtml) {
    try {
      if (cartDrawerHtml) {
        var drawerDoc = new DOMParser().parseFromString(cartDrawerHtml, 'text/html');
        var freshCartDrawer = drawerDoc.querySelector('#CartDrawer');
        var liveCartDrawer = document.getElementById('CartDrawer');
        if (freshCartDrawer && liveCartDrawer) {
          liveCartDrawer.innerHTML = freshCartDrawer.innerHTML;
        }

        // Mirror CartDrawer.refreshCartContent()'s empty-state class sync.
        var freshCartDrawerEl = drawerDoc.querySelector('cart-drawer');
        var liveCartDrawerEl = document.querySelector('cart-drawer');
        if (freshCartDrawerEl && liveCartDrawerEl) {
          if (freshCartDrawerEl.classList.contains('is-empty')) {
            liveCartDrawerEl.classList.add('is-empty');
          } else {
            liveCartDrawerEl.classList.remove('is-empty');
          }
        }
      }
    } catch (e) {
      debugLog('failed to apply cart-drawer section update', e);
    }

    applyCartIconBubbleUpdate(cartIconBubbleHtml);
  }

  class CartRecommendations extends HTMLElement {
    constructor() {
      super();
    }
  }

  CartRecommendations.prototype.connectedCallback = function () {
    if (this._connected) return;
    this._connected = true;

    this._renderToken = 0;
    this._renderTimer = null;
    this._boundOnCartChanged = this._scheduleRender.bind(this);

    var self = this;
    var init = function () {
      if (self.dataset.source === 'manual') {
        // Manual mode (the /pages/preporuceno recommendations page): the page
        // controller feeds items via setItems() directly, so there is no
        // engine cascade to run and no cart mutation elsewhere on the page
        // that should trigger a re-render.
        return;
      }
      document.addEventListener('mtgreco:cart-changed', self._boundOnCartChanged);
      if (self.dataset.layout === 'drawer') {
        // Drawer instances exist in the DOM of EVERY page, closed almost all of
        // the time. Rendering on connect would run the whole cascade (up to a
        // /cart.js plus 8 product fetches) on every page load for markup nobody
        // is looking at, so wait until the drawer is actually opened.
        self._renderWhenDrawerOpens();
      } else {
        self._render();
      }
    };

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', init, { once: true });
    } else {
      init();
    }
  };

  /**
   * Renders as soon as the enclosing <cart-drawer> host is open. The theme's
   * CartDrawer.open() marks the drawer open by adding the "active" class to the
   * host element (assets/cart-drawer.js, mirrored in theme/_reference/), and
   * close() removes it - so a one-shot observer on that attribute is the
   * cheapest reliable "drawer opened" signal, and it does not depend on the
   * theme also re-rendering the drawer section.
   */
  CartRecommendations.prototype._renderWhenDrawerOpens = function () {
    var host = this.closest('cart-drawer');
    if (!host) {
      // Not actually inside the theme's drawer (markup changed?) - degrade to
      // the old eager behaviour rather than never rendering.
      this._render();
      return;
    }
    if (host.classList.contains('active')) {
      // Already open: this instance was created by a section refresh performed
      // while the drawer was on screen.
      this._render();
      return;
    }

    var self = this;
    try {
      this._drawerObserver = new MutationObserver(function () {
        if (!host.classList.contains('active')) return;
        self._disconnectDrawerObserver(); // one-shot
        self._render();
      });
      this._drawerObserver.observe(host, { attributes: true, attributeFilter: ['class'] });
    } catch (e) {
      debugLog('MutationObserver unavailable, rendering eagerly', e);
      this._render();
    }
  };

  CartRecommendations.prototype._disconnectDrawerObserver = function () {
    if (!this._drawerObserver) return;
    try {
      this._drawerObserver.disconnect();
    } catch (e) {
      // no-op
    }
    this._drawerObserver = null;
  };

  CartRecommendations.prototype.disconnectedCallback = function () {
    this._connected = false;
    document.removeEventListener('mtgreco:cart-changed', this._boundOnCartChanged);
    this._disconnectDrawerObserver();
    if (this._imp) this._imp.reset();
    if (this._renderTimer) {
      window.clearTimeout(this._renderTimer);
      this._renderTimer = null;
    }
    if (this._errorTimer) {
      window.clearTimeout(this._errorTimer);
      this._errorTimer = null;
    }
  };

  // Which block this is, for measurement: the page sets data-reco-row per row
  // (graph / continue / copurchase / popular); the drawer and cart page default.
  CartRecommendations.prototype._rowKey = function () {
    if (this.dataset.recoRow) return this.dataset.recoRow;
    if (this.dataset.layout === 'drawer') return 'drawer';
    return this.dataset.source === 'manual' ? 'page' : 'cart';
  };

  CartRecommendations.prototype._impressions = function () {
    var T = tracker();
    if (!T) return null;
    // A row can be relabelled (the page reuses the graph row for "Popular"),
    // so the tracker is rebuilt whenever the key changes.
    var row = this._rowKey();
    if (!this._imp || this._impRow !== row) {
      if (this._imp) this._imp.reset();
      this._imp = T.impressionTracker(row);
      this._impRow = row;
    }
    return this._imp;
  };

  // Debounces re-render requests so an in-section add-to-cart (which we
  // re-render explicitly) and the tracker's mtgreco:cart-changed event
  // (which can fire for the same mutation) never cause a double fetch.
  CartRecommendations.prototype._scheduleRender = function () {
    var self = this;
    if (this._renderTimer) window.clearTimeout(this._renderTimer);
    this._renderTimer = window.setTimeout(function () {
      self._renderTimer = null;
      self._render();
    }, 300);
  };

  // Manual mode (data-source="manual"): the caller (the recommendations page
  // controller) already has a resolved item list - no engine call, just
  // render. Mirrors the tail of _render()'s .then() handler.
  CartRecommendations.prototype.setItems = function (items) {
    this._renderToken++;
    if (!items || !items.length) {
      this._clearList();
      this.hidden = true;
      return;
    }
    this._renderItems(items);
    this.hidden = false;
  };

  // Manual mode "Load more": appends `items` after whatever is already
  // rendered instead of replacing the list. _renderItems() always clears
  // and rebuilds the list from scratch, so the existing children are pulled
  // out first and reinserted, in their original order, ahead of the newly
  // rendered ones.
  CartRecommendations.prototype.setItemsAppend = function (items) {
    var list = this.querySelector('.reco-cart__list');
    if (!list) return;
    var existing = Array.prototype.slice.call(list.children);
    this._renderItems(items, existing.length); // renders only the new items, positions continue
    existing.reverse().forEach(function (n) {
      list.insertBefore(n, list.firstChild);
    });
    this.hidden = !list.children.length;
  };

  CartRecommendations.prototype._render = function () {
    var self = this;
    var token = ++this._renderToken;

    var limit = parseInt(this.dataset.limit, 10);
    if (!isFinite(limit) || limit <= 0) limit = 3;
    var fallbackCollection = this.dataset.fallbackCollection || 'commander-staples';

    var getRecommendations =
      window.MtgReco && typeof window.MtgReco.getRecommendations === 'function'
        ? window.MtgReco.getRecommendations
        : null;

    var promise;
    if (getRecommendations) {
      try {
        promise = getRecommendations({ limit: limit, fallbackCollection: fallbackCollection });
      } catch (err) {
        // A synchronous throw out of getRecommendations() must not break the
        // block - log it and fall back to an empty result, same as an
        // asynchronous rejection handled below.
        debugLog('getRecommendations threw synchronously', err);
        promise = Promise.resolve([]);
      }
    } else {
      promise = Promise.resolve([]);
    }

    promise
      .then(function (items) {
        if (token !== self._renderToken) return; // a newer render superseded this one
        if (!items || !items.length) {
          self._clearList();
          self.hidden = true;
          return;
        }
        self._renderItems(items);
        self.hidden = false;
      })
      .catch(function (err) {
        debugLog('getRecommendations failed', err);
        if (token !== self._renderToken) return;
        self._clearList();
        self.hidden = true;
      });
  };

  CartRecommendations.prototype._clearList = function () {
    var list = this.querySelector('.reco-cart__list');
    if (list) list.innerHTML = '';
    if (this._imp) this._imp.reset();
  };

  // `offset` = position of the first item in the row (Load more appends after
  // the cards already shown); absent = a fresh list, so pending observations
  // of the previous tiles are dropped.
  CartRecommendations.prototype._renderItems = function (items, offset) {
    var list = this.querySelector('.reco-cart__list');
    var template = this.querySelector('.reco-cart__card-template');
    if (!list || !template || !template.content) return;

    list.innerHTML = '';
    var base = offset > 0 ? offset : 0;
    var imp = this._impressions();
    if (imp && !(offset > 0)) imp.reset();
    var rowKey = this._rowKey();

    var addLabel = this.dataset.addLabel || 'Add to cart';
    var currency = getShopCurrency();
    // Drawer tiles (Task 5) are image + button only - no title, no price -
    // per the drawer's minimal layout requirement. (A heading renders once
    // above the tile list, outside this per-tile template - see the <h2> in
    // snippets/cart-drawer.liquid.) The cart-page layout is untouched:
    // title/price are still populated below whenever this is not the drawer
    // instance.
    var isDrawer = this.dataset.layout === 'drawer';

    for (var i = 0; i < items.length; i++) {
      var item = items[i];
      if (!item || !item.handle) continue;

      var node = template.content.firstElementChild.cloneNode(true);

      var imageLink = node.querySelector('.reco-cart__image-link');
      var image = node.querySelector('.reco-cart__image');
      var titleLink = node.querySelector('.reco-cart__title-link');
      var title = node.querySelector('.reco-cart__title');
      var price = node.querySelector('.reco-cart__price');
      var addButton = node.querySelector('.reco-cart__button--add');

      if (imageLink) imageLink.href = item.url;
      if (image) {
        if (item.image) {
          image.src = item.image;
          image.alt = item.title || '';
        } else {
          // No featured image (rare, but real - some singles have none). A
          // src-less <img> renders as a broken-image placeholder inside a
          // full-size padded box, so hide the wrapper entirely and let the
          // card's body take the space. Inline display:none is used because
          // the [hidden] attribute loses to .reco-cart__image-wrap's own
          // `display: flex` / .reco-cart__image-link's `display: block`.
          var imageWrap = node.querySelector('.reco-cart__image-wrap');
          if (imageWrap) imageWrap.style.display = 'none';
          if (imageLink) imageLink.style.display = 'none';
          if (image.parentNode) image.parentNode.removeChild(image);
        }
      }
      if (isDrawer) {
        // titleLink wraps title (<a class="reco-cart__title-link"><h3 .../></a>),
        // so removing it also removes the title node in one step.
        if (titleLink && titleLink.parentNode) titleLink.parentNode.removeChild(titleLink);
        if (price && price.parentNode) price.parentNode.removeChild(price);
        // The drawer shows no title/price, but "already viewed" / "was in your cart"
        // (reco-engine.js levels 1-2) still explains why the card is there.
        var body = node.querySelector('.reco-cart__body');
        if (item.reason && body) {
          var seenEl = document.createElement('p');
          seenEl.className = 'reco-cart__reason';
          seenEl.textContent = item.reason;
          body.insertBefore(seenEl, body.firstChild);
        }
      } else {
        if (titleLink) titleLink.href = item.url;
        if (title) title.textContent = item.title || '';
        if (price) price.textContent = formatPrice(item.price, item.currency || currency);
        // Optional one-line reason under the price: the graph row on /pages/preporuceno
        // ("Goes with X"), "already viewed" on viewed / removed-from-cart cards.
        if (item.reason && price && price.parentNode) {
          var reasonEl = document.createElement('p');
          reasonEl.className = 'reco-cart__reason';
          reasonEl.textContent = item.reason;
          price.parentNode.insertBefore(reasonEl, price.nextSibling);
        }
      }

      // Every tile is a direct add-to-cart - the engine already chose a single
      // variantId/price per product (Near Mint first, see reco-engine.js
      // normalizeProduct), so there is no "choose options" branch here anymore.
      var pos = base + i;
      if (addButton) {
        var addLabelEl = addButton.querySelector('.reco-cart__button-label');
        if (addLabelEl) addLabelEl.textContent = addLabel;
        addButton.addEventListener('click', this._onAddClick.bind(this, item, addButton, pos));
      }

      if (tracker()) {
        // Bound per tile: `item`/`pos` are loop variables shared by every iteration.
        var onOpen = (function (it, p) {
          return function () {
            if (imp) imp.flush();
            tracker().event('mtgreco_click', rowKey, it, p);
          };
        })(item, pos);
        [imageLink, titleLink].forEach(function (a) {
          if (a && a.parentNode) a.addEventListener('click', onOpen);
        });
        if (imp) imp.observe(node, item, pos);
      }

      if (this.dataset.source === 'manual') {
        var self = this;
        // `item` is the shared loop variable (function-scoped `var`), so a
        // closure over it directly would report the LAST card's handle for
        // every click - capture the handle per iteration instead.
        var h = item.handle;
        [imageLink, titleLink].forEach(function (a) {
          if (a) {
            a.addEventListener('click', function () {
              self.dispatchEvent(new CustomEvent('reco:click', { detail: { handle: h }, bubbles: true }));
            });
          }
        });
      }

      list.appendChild(node);
    }
  };

  CartRecommendations.prototype._onAddClick = function (item, button, pos) {
    if (button.disabled) return;
    var self = this;
    var isDrawer = this.dataset.layout === 'drawer';
    var T = tracker();
    var rowKey = this._rowKey();
    var addBody = { id: item.variantId, quantity: 1 };
    // Hidden "_reco" line item property: rides along to the order, which is how
    // purchases are attributed to a row/link type (index-builder/lib/reco-stats.mjs).
    if (T) addBody.properties = { _reco: T.lineProperty(rowKey, T.typeOf(item), T.abBucket()) };

    button.disabled = true;
    button.classList.add('reco-cart__button--loading');

    // Plain add (no "sections" in the same POST): requesting sections in the
    // same /cart/add.js call can - observed live, specifically on an
    // empty-to-non-empty cart transition - return section HTML rendered
    // against the pre-add cart state (a Shopify-side race), which would
    // leave the on-page cart totals/count stale even though the add itself
    // succeeded. Fetching each section separately via the Section
    // Rendering API (?section_id=<id>, text/html) AFTER the add resolves
    // avoids that race entirely and matches the theme's own already-working
    // pattern (assets/cart.js CartItems.onCartUpdate / assets/cart-drawer.js
    // CartDrawer.refreshCartContent / assets/product-form.js
    // updateCartIcons all do the same two-step add-then-fetch).
    window
      .fetch(getCartAddUrl(), {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify(addBody)
      })
      .then(function (response) {
        return response.json().then(function (data) {
          if (!response.ok) {
            var err = new Error((data && data.description) || 'add to cart failed');
            err.data = data;
            throw err;
          }
          return data;
        });
      })
      .then(function () {
        if (T) T.event('mtgreco_add', rowKey, item, pos);
        // The cart just changed, so the engine's short-lived memo of the
        // previous recommendation list is stale - it still contains the product
        // that was just added. The tracker dispatches mtgreco:cart-changed for
        // this add too (which also invalidates), but do not rely on the tracker
        // being present: reco-cart.js and reco-engine.js load together, while
        // reco-tracker.js is wired in separately from layout/theme.liquid.
        try {
          if (window.MtgReco && typeof window.MtgReco.invalidateRecommendations === 'function') {
            window.MtgReco.invalidateRecommendations();
          }
        } catch (e) {
          debugLog('invalidateRecommendations failed', e);
        }

        if (self.dataset.source === 'manual') {
          // Manual mode (recommendations page): no "cart-items"/"cart-footer"
          // sections exist on this page to refresh, so only the header bubble
          // is fetched, and there is no reload fallback - there is no stale
          // cart markup on the page for a failed section fetch to leave behind.
          return fetchSectionHTML('cart-icon-bubble').then(function (html) {
            applyCartIconBubbleUpdate(html);
            self.dispatchEvent(new CustomEvent('reco:add', { detail: { handle: item.handle }, bubbles: true }));
            var node = button.closest('.reco-cart__item');
            if (node) node.remove();
            var list = self.querySelector('.reco-cart__list');
            self.hidden = !(list && list.children.length);
          });
        }

        if (isDrawer) {
          return Promise.all([fetchSectionHTML('cart-drawer'), fetchSectionHTML('cart-icon-bubble')]).then(function (
            results
          ) {
            // The add itself already succeeded - if the post-add section
            // fetch failed (fetchSectionHTML resolved null), the DOM would
            // otherwise silently keep showing the pre-add cart, so force a
            // full reload rather than leave the shopper looking at stale state.
            if (!results[0]) {
              window.location.reload();
              return;
            }
            // Replaces this element's own markup (see applyDrawerSectionUpdate
            // doc comment) - do not touch `self` after this, a fresh
            // <cart-recommendations> instance renders itself instead.
            applyDrawerSectionUpdate(results[0], results[1]);
          });
        }
        return Promise.all([
          fetchSectionHTML(getRuntimeSectionId('main-cart-items', 'main-cart-items')),
          fetchSectionHTML(getRuntimeSectionId('main-cart-footer', 'main-cart-footer')),
          fetchSectionHTML('cart-icon-bubble')
        ]).then(function (results) {
          // Same stale-UI guard as the drawer branch above, for the required
          // cart-items/cart-footer sections (cart-icon-bubble stays optional).
          if (!results[0] || !results[1]) {
            window.location.reload();
            return;
          }
          applySectionUpdates({
            'cart-items': results[0],
            'cart-footer': results[1],
            'cart-icon-bubble': results[2]
          });
          self._scheduleRender();
        });
      })
      .catch(function (err) {
        debugLog('add to cart failed', err);
        self._showAddError(button);
      })
      .finally(function () {
        button.disabled = false;
        button.classList.remove('reco-cart__button--loading');
      });
  };

  // Shows an error label on the add button for ~3s, then restores the
  // normal "add to cart" label. The button is already re-enabled by the
  // caller's .finally() before/around this runs.
  CartRecommendations.prototype._showAddError = function (button) {
    var labelEl = button.querySelector('.reco-cart__button-label');
    if (!labelEl) return;

    var self = this;
    var errorLabel = this.dataset.errorLabel || 'Error, try again';
    var addLabel = this.dataset.addLabel || 'Add to cart';

    labelEl.textContent = errorLabel;
    button.classList.add('reco-cart__btn--error');

    // Track the timer on the instance (and clear any prior one) so two quick
    // failed clicks don't race two overlapping reverts, and so
    // disconnectedCallback can cancel it if the element goes away first.
    if (this._errorTimer) window.clearTimeout(this._errorTimer);
    this._errorTimer = window.setTimeout(function () {
      self._errorTimer = null;
      if (!button.isConnected) return;
      labelEl.textContent = addLabel;
      button.classList.remove('reco-cart__btn--error');
    }, 3000);
  };

  window.customElements.define('cart-recommendations', CartRecommendations);

  // ------------------------------------------------------------------
  // Drawer + bfcache guard (iOS Safari). The theme's cart-drawer.js only
  // toggles the 'active' class; it never listens for pagehide/pageshow. On
  // iOS, leaving the page with the drawer open (e.g. tapping "View cart" in
  // the drawer) and coming back via the back gesture restores the page from
  // the back/forward cache with the drawer still 'active' and the dark
  // overlay stuck on screen. Close the drawer on the way out and again on a
  // cached restore so the snapshot and the restored page are both clean.
  // Theme-level behaviour shipped with the reco feature; remove with it.
  // ------------------------------------------------------------------
  function closeCartDrawerIfOpen() {
    try {
      var drawer = document.querySelector('cart-drawer');
      if (drawer && drawer.classList.contains('active')) {
        if (typeof drawer.close === 'function') drawer.close();
        else drawer.classList.remove('active');
      }
      document.body.classList.remove('overflow-hidden');
    } catch (e) {
      /* never break the page */
    }
  }
  window.addEventListener('pagehide', closeCartDrawerIfOpen);
  window.addEventListener('pageshow', function (ev) {
    if (ev && ev.persisted) closeCartDrawerIfOpen();
  });

  // ------------------------------------------------------------------
  // iOS touch scroll-lock while the drawer is open. On iOS Safari,
  // body { overflow: hidden } does NOT stop the page behind a fixed drawer
  // from scrolling: a finger on any non-scrollable part of the drawer (the
  // free-shipping bar, the footer with View cart / Checkout) pans the page.
  // Only <cart-drawer-items> (the theme's overflow:auto list) may scroll;
  // every other touch inside the open drawer is cancelled, and scrolling the
  // list is stopped at its top/bottom edge so it never chains to the page.
  // Theme-level behaviour shipped with the reco feature; remove with it.
  // ------------------------------------------------------------------
  (function installDrawerScrollLock() {
    var startY = 0;

    function openDrawer() {
      var d = document.querySelector('cart-drawer');
      return d && d.classList.contains('active') ? d : null;
    }

    document.addEventListener(
      'touchstart',
      function (ev) {
        if (!openDrawer()) return;
        if (ev.touches && ev.touches.length) startY = ev.touches[0].clientY;
      },
      { passive: true }
    );

    document.addEventListener(
      'touchmove',
      function (ev) {
        try {
          var drawer = openDrawer();
          if (!drawer) return;
          var target = ev.target;
          var scroller = target && target.closest ? target.closest('cart-drawer-items') : null;

          // Touch outside the list (backdrop, header, free-shipping bar,
          // footer, or anywhere outside the drawer): never scroll the page.
          if (!scroller || !drawer.contains(target)) {
            if (ev.cancelable) ev.preventDefault();
            return;
          }

          // Inside the list: allow scrolling, but block the move that would
          // chain past the list's own edge (iOS would hand it to the page).
          var y = ev.touches && ev.touches.length ? ev.touches[0].clientY : startY;
          var movingDown = y > startY; // finger moves down => content scrolls up (towards top)
          var atTop = scroller.scrollTop <= 0;
          var atBottom = scroller.scrollTop + scroller.clientHeight >= scroller.scrollHeight - 1;
          var cannotScroll = scroller.scrollHeight <= scroller.clientHeight;
          if (cannotScroll || (movingDown && atTop) || (!movingDown && atBottom)) {
            if (ev.cancelable) ev.preventDefault();
          }
        } catch (e) {
          /* never break the page */
        }
      },
      { passive: false }
    );
  })();
})();
